import { useMemo } from "react";

import {
    ActionType,
    FarmingInfoExpanded,
    LoopscalePointsAction,
    useFarmingInfoByActionTypeAndAddress
} from "@bridgesplit/abf-react";
import { ExpandedTooltip, Row, TooltipSection } from "@bridgesplit/ui";
import { PointDisplay, RateDisplay } from "app/components/points";
import { RewardsDailyPoints } from "app/components/points/app/daily-rewards";
import { RewardsProtocolPoints } from "app/components/points/app/RewardsProtocolPoints";

import { MarketStats } from "./types";

export function NetBorrowApy({ data }: { data: MarketStats | undefined }) {
    const { farmingInfo, hasFarmingInfo } = useFarmingInfoByActionTypeAndAddress(
        data?.metadata?.mint,
        ActionType.Borrow
    );

    const sections = useMemo(() => {
        return getRewardsSections({
            mint: data?.metadata?.mint,
            action: LoopscalePointsAction.Borrow,
            farmingInfo,
            hasFarmingInfo
        });
    }, [data, farmingInfo, hasFarmingInfo]);

    return (
        <ExpandedTooltip sections={sections}>
            <Row spacing={1}>
                <RewardsProtocolPoints farmingInfo={farmingInfo} displayType="badge" />
                <PointDisplay address={data?.metadata?.mint} loopscalePointsAction={LoopscalePointsAction.Borrow} />
                <RateDisplay apy={data?.wAvgBorrowApy} variant="body1" color="body" />
            </Row>
        </ExpandedTooltip>
    );
}

export function NetLendApy({ data }: { data: MarketStats | undefined }) {
    const { farmingInfo, hasFarmingInfo } = useFarmingInfoByActionTypeAndAddress(data?.metadata?.mint, ActionType.Lend);

    const sections = useMemo(() => {
        return getRewardsSections({
            mint: data?.metadata?.mint,
            action: LoopscalePointsAction.Lend,
            farmingInfo,
            hasFarmingInfo
        });
    }, [data, farmingInfo, hasFarmingInfo]);

    return (
        <ExpandedTooltip sections={sections}>
            <Row spacing={1}>
                <PointDisplay address={data?.metadata?.mint} loopscalePointsAction={LoopscalePointsAction.Lend} />
                <RateDisplay apy={data?.wAvgLendApy} variant="body1" color="body" />
            </Row>
        </ExpandedTooltip>
    );
}

function getRewardsSections({
    mint,
    action,
    farmingInfo,
    hasFarmingInfo
}: {
    mint: string | undefined;
    action: LoopscalePointsAction;
    farmingInfo: FarmingInfoExpanded | undefined;
    hasFarmingInfo: boolean;
}): TooltipSection[] {
    const sections: TooltipSection[] = [];

    sections.push({
        type: "render",
        render: () => <RewardsDailyPoints loopscalePointsAction={action} identifier={mint} />,
        px: 1.5,
        spacing: 0.5
    });

    if (hasFarmingInfo) {
        sections.push({ type: "divider" });
        sections.push({
            type: "render",
            render: () => <RewardsProtocolPoints farmingInfo={farmingInfo} />,
            px: 1.5,
            spacing: 0.5
        });
    }

    return sections;
}
